<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import DetailsSheet from '@/components/editing/DetailsSheet.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import { Textarea } from '@/components/ui/textarea';
import ResponsiveActionSubmenu from '@/components/ui/responsive-action-submenu/ResponsiveActionSubmenu.vue';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CheckSquare, ChevronDown, ChevronUp, FolderOpen, FolderPlus, FolderTree, ListChecks, MoreHorizontal, Sparkles, ThumbsDown, ThumbsUp, Trash2 } from 'lucide-vue-next';
import type { AssistantFeedbackRating } from '~/composables/useAssistantFeedback';
import type { AssistantSuggestionApplyInput, AssistantSuggestionRefinementInput } from '~/lib/assistantSuggestionUi';
import { assistantActionBadgeClass, assistantActionLabel, assistantConfidenceBadgeClass, assistantPriorityLabel } from '~/lib/assistantSuggestionUi';
import type { AssistantSuggestion } from '~/types/models';

const props = withDefaults(defineProps<{
    suggestion: AssistantSuggestion;
    taskTitles?: string[];
    taskSuggestions?: AssistantSuggestion[];
    applying?: boolean;
    refining?: boolean;
    buildingProjectPlan?: boolean;
    applyLabel: string;
    compact?: boolean;
    collapsible?: boolean;
    defaultCollapsed?: boolean;
    canRefine?: boolean;
    canBuildProjectPlan?: boolean;
    projectOptions?: Array<{ id: string; name: string }>;
    evidenceItems?: string[];
}>(), {
    applying: false,
    refining: false,
    buildingProjectPlan: false,
    compact: false,
    collapsible: false,
    defaultCollapsed: false,
    canRefine: false,
    canBuildProjectPlan: false,
    projectOptions: () => [],
    taskSuggestions: () => [],
    evidenceItems: () => [],
});

const emit = defineEmits<{
    (e: 'apply', payload: AssistantSuggestionApplyInput): void;
    (e: 'dismiss'): void;
    (e: 'dismiss-selected', taskIds: string[]): void;
    (e: 'refine', payload: AssistantSuggestionRefinementInput): void;
    (e: 'build-project-plan'): void;
    (e: 'feedback', rating: AssistantFeedbackRating): void;
}>();

const expanded = ref(!props.defaultCollapsed);
const refineDialogOpen = ref(false);
const reviewDialogOpen = ref(false);
const refinementText = ref('');
const selectedProjectId = ref(props.suggestion.projectId || '');
const selectedTaskIds = ref<string[]>([]);
const pendingApply = ref<AssistantSuggestionApplyInput | null>(null);
const feedbackRating = ref<AssistantFeedbackRating | null>(null);

const showBody = computed(() => !props.collapsible || expanded.value);
const showProjectSelector = computed(() => props.projectOptions.length > 0);
const projectMenuOptions = computed(() => props.projectOptions.map(project => ({ value: project.id, label: project.name })));
const projectActionMenuItems = computed(() => props.projectOptions.map(project => ({
    id: project.id,
    label: project.name,
    icon: FolderOpen,
})));
const taskItems = computed(() => {
    if (props.taskSuggestions.length > 0) {
        return props.taskSuggestions.map((suggestion, index) => ({
            id: suggestion.id || `${props.suggestion.id}-task-${index}`,
            title: suggestion.suggestedTaskTitle || suggestion.title,
        }));
    }

    return (props.taskTitles || []).map((title, index) => ({
        id: `${props.suggestion.id}-task-${index}`,
        title,
    }));
});
const hasTaskChecklist = computed(() => taskItems.value.length > 0);
const selectedTaskCount = computed(() => selectedTaskIds.value.length);
const allTasksSelected = computed(() => hasTaskChecklist.value && selectedTaskCount.value === taskItems.value.length);
const canActOnSelection = computed(() => !hasTaskChecklist.value || selectedTaskCount.value > 0);
const selectedReviewTasks = computed(() => taskItems.value.filter(task => selectedTaskIds.value.includes(task.id)));
const effectiveProjectName = computed(() => props.suggestion.suggestedProjectName || props.suggestion.topicLabel || props.suggestion.title);
const pendingProjectName = computed(() => {
    const projectId = pendingApply.value?.projectId;
    return projectId
        ? props.projectOptions.find(project => project.id === projectId)?.name || 'the selected project'
        : '';
});
const reviewDestinationLabel = computed(() => {
    if (pendingApply.value?.destination === 'existing_project') {
        return `Add to ${pendingProjectName.value}`;
    }
    if (pendingApply.value?.destination === 'new_project') {
        return `Create ${effectiveProjectName.value} as a new project`;
    }
    return props.suggestion.kind === 'new_project'
        ? `Create ${effectiveProjectName.value}`
        : props.applyLabel;
});
const refinementInputId = computed(() => `assistant-refinement-${props.suggestion.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`);

watch(
    () => props.taskSuggestions,
    (nextSuggestions) => {
        selectedTaskIds.value = (nextSuggestions || []).map((suggestion) => suggestion.id).filter(Boolean);
    },
    { immediate: true, deep: true },
);

watch(
    () => props.suggestion,
    (nextSuggestion) => {
        selectedProjectId.value = nextSuggestion.projectId || '';
    },
    { immediate: true },
);

function toggleExpanded() {
    if (!props.collapsible) return;
    expanded.value = !expanded.value;
}

function toggleTaskSelection(taskId: string) {
    selectedTaskIds.value = selectedTaskIds.value.includes(taskId)
        ? selectedTaskIds.value.filter((value) => value !== taskId)
        : [...selectedTaskIds.value, taskId];
}

function selectAllTasks() {
    selectedTaskIds.value = taskItems.value.map((task) => task.id);
}

function clearTaskSelection() {
    selectedTaskIds.value = [];
}

function applySelection(destination: 'default' | 'new_project' | 'existing_project', projectId?: string) {
    pendingApply.value = {
        destination,
        projectId,
        selectedTaskIds: hasTaskChecklist.value ? selectedTaskIds.value : undefined,
    };
    reviewDialogOpen.value = true;
}

function confirmApplySelection() {
    if (!pendingApply.value) return;
    emit('apply', pendingApply.value);
    reviewDialogOpen.value = false;
    pendingApply.value = null;
}

function applyToExistingProject(item: { id: string }) {
    applySelection('existing_project', item.id);
}

function dismissSelectedTasks() {
    if (!hasTaskChecklist.value || selectedTaskCount.value === 0) return;
    emit('dismiss-selected', selectedTaskIds.value);
}

function openRefineDialog() {
    refineDialogOpen.value = true;
}

function submitRefinement() {
    const instructions = refinementText.value.trim();
    if (!instructions) return;

    emit('refine', {
        instructions,
        projectId: selectedProjectId.value || undefined,
    });
    refineDialogOpen.value = false;
    refinementText.value = '';
}

function badgeCountLabel(count: number) {
    return count > 0 ? `${count}` : '';
}

function submitFeedback(rating: AssistantFeedbackRating) {
    feedbackRating.value = rating;
    emit('feedback', rating);
}
</script>

<template>
    <div class="rounded-xl border border-border/60 bg-slate-100/95 p-2.5 dark:bg-[color:color-mix(in_srgb,var(--background)_86%,#020617_14%)]">
        <div class="flex items-start justify-between gap-2">
            <div class="space-y-1.5">
                <div class="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" :class="assistantActionBadgeClass(suggestion)">
                        {{ assistantActionLabel(suggestion) }}
                    </Badge>
                    <Badge variant="outline" :class="assistantConfidenceBadgeClass(suggestion.confidence)">
                        {{ assistantPriorityLabel(suggestion.confidence) }}
                    </Badge>
                </div>
                <h4 class="text-sm font-semibold">{{ suggestion.title }}</h4>
                <p class="text-sm text-muted-foreground" :class="showBody ? '' : 'line-clamp-2'">
                    {{ compact ? suggestion.summary : (suggestion.summary || suggestion.rationale) }}
                </p>
            </div>

            <Button
                v-if="collapsible"
                size="icon"
                variant="ghost"
                class="mt-0.5 h-6 w-6 rounded-full border border-border/30 bg-white/80 text-muted-foreground hover:bg-accent/60 hover:text-foreground dark:bg-background/55"
                @click="toggleExpanded"
            >
                <ChevronUp v-if="showBody" class="size-3.5" />
                <ChevronDown v-else class="size-3.5" />
                <span class="sr-only">{{ showBody ? 'Collapse suggestion' : 'Expand suggestion' }}</span>
            </Button>
        </div>

        <template v-if="showBody">
            <p v-if="!compact && suggestion.rationale" class="mt-2.5 text-xs text-muted-foreground">{{ suggestion.rationale }}</p>

            <div v-if="evidenceItems.length" class="mt-2.5 border-t border-border/50 pt-2.5">
                <p class="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Based on</p>
                <ul class="mt-1.5 space-y-1.5">
                    <li
                        v-for="(evidence, index) in evidenceItems"
                        :key="`${suggestion.id}-evidence-${index}`"
                        class="border-l-2 border-border/70 pl-2 text-xs leading-5 text-muted-foreground"
                    >
                        “{{ evidence }}”
                    </li>
                </ul>
            </div>

            <div v-if="suggestion.suggestedProjectName" class="mt-2.5 border-t border-border/50 pt-2.5">
                <p class="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Suggested project</p>
                <p class="mt-1 text-sm font-medium">{{ suggestion.suggestedProjectName }}</p>
            </div>

            <div v-if="hasTaskChecklist" class="mt-2.5 border-t border-border/50 pt-2.5">
                <div class="mb-2 flex items-center justify-between gap-2">
                    <div>
                        <p class="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Suggested tasks</p>
                        <p class="mt-1 text-sm text-foreground/90">
                            {{ selectedTaskCount }} of {{ taskItems.length }} selected
                        </p>
                    </div>
                    <div class="flex items-center gap-1">
                        <Button size="sm" variant="ghost" class="h-7 px-2 text-xs" @click="allTasksSelected ? clearTaskSelection() : selectAllTasks()">
                            {{ allTasksSelected ? 'Clear' : 'Select all' }}
                        </Button>
                    </div>
                </div>

                <div class="space-y-1.5">
                    <label
                        v-for="task in taskItems"
                        :key="task.id"
                        class="flex items-start gap-3 rounded-lg border border-border/40 bg-background/55 px-3 py-2 text-sm transition-colors hover:bg-accent/40 dark:bg-background/25"
                    >
                        <input
                            type="checkbox"
                            class="mt-0.5 h-4 w-4 rounded border-border/60"
                            :checked="selectedTaskIds.includes(task.id)"
                            @change="toggleTaskSelection(task.id)"
                        >
                        <span>{{ task.title }}</span>
                    </label>
                </div>
            </div>

            <div class="mt-2.5 flex flex-wrap items-center gap-2">
                <DropdownMenu>
                    <DropdownMenuTrigger as-child>
                        <Button size="sm" :disabled="applying || refining || buildingProjectPlan">
                            {{ applying ? 'Working...' : 'Review actions' }}
                            <MoreHorizontal class="ml-1 size-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" class="w-64">
                        <DropdownMenuItem
                            v-if="hasTaskChecklist"
                            class="justify-between gap-3"
                            :disabled="!canActOnSelection"
                            @select="applySelection('new_project')"
                        >
                            <span class="flex items-center gap-2">
                                <FolderPlus class="size-4" />
                                <span>Add to new project</span>
                            </span>
                            <span class="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                                {{ badgeCountLabel(selectedTaskCount) }}
                            </span>
                        </DropdownMenuItem>

                        <ResponsiveActionSubmenu
                            v-if="showProjectSelector"
                            mode="dropdown"
                            label="Add to existing project"
                            :icon="FolderOpen"
                            :items="projectActionMenuItems"
                            :trigger-meta="badgeCountLabel(selectedTaskCount)"
                            :disabled="!canActOnSelection"
                            content-class="w-56"
                            inline
                            @select="applyToExistingProject"
                        />

                        <DropdownMenuItem
                            v-if="hasTaskChecklist"
                            class="justify-between gap-3"
                            :disabled="!canActOnSelection"
                            @select="applySelection('default')"
                        >
                            <span class="flex items-center gap-2">
                                <ListChecks class="size-4" />
                                <span>Create selected tasks</span>
                            </span>
                            <span class="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                                {{ badgeCountLabel(selectedTaskCount) }}
                            </span>
                        </DropdownMenuItem>

                        <DropdownMenuItem
                            v-else
                            @select="applySelection(suggestion.kind === 'new_project' ? 'new_project' : 'default')"
                        >
                            <FolderPlus v-if="suggestion.kind === 'new_project'" class="size-4" />
                            <ListChecks v-else class="size-4" />
                            {{ suggestion.kind === 'new_project' ? `Create "${effectiveProjectName}"` : applyLabel }}
                        </DropdownMenuItem>

                        <DropdownMenuItem
                            v-if="hasTaskChecklist"
                            class="justify-between gap-3"
                            :disabled="!canActOnSelection"
                            @select="dismissSelectedTasks"
                        >
                            <span class="flex items-center gap-2">
                                <Trash2 class="size-4" />
                                <span>Dismiss selected tasks</span>
                            </span>
                            <span class="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                                {{ badgeCountLabel(selectedTaskCount) }}
                            </span>
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />

                        <DropdownMenuItem v-if="canBuildProjectPlan" @select="$emit('build-project-plan')">
                            <FolderTree class="size-4" />
                            {{ buildingProjectPlan ? 'Building project plan...' : 'Build project plan' }}
                        </DropdownMenuItem>
                        <DropdownMenuItem v-if="canRefine" @select="openRefineDialog">
                            <Sparkles class="size-4" />
                            Refine with AI
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            v-if="hasTaskChecklist"
                            @select="allTasksSelected ? clearTaskSelection() : selectAllTasks()"
                        >
                            <CheckSquare class="size-4" />
                            {{ allTasksSelected ? 'Clear task selection' : 'Select all tasks' }}
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />

                        <DropdownMenuItem variant="destructive" @select="$emit('dismiss')">
                            <Trash2 class="size-4" />
                            Dismiss suggestion
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
                <div class="ml-auto flex items-center gap-0.5" aria-label="Rate this suggestion">
                    <Button
                        size="icon"
                        variant="ghost"
                        class="size-7"
                        :class="feedbackRating === 'helpful' ? 'text-primary' : 'text-muted-foreground'"
                        :aria-pressed="feedbackRating === 'helpful'"
                        aria-label="Mark suggestion helpful"
                        @click="submitFeedback('helpful')"
                    >
                        <ThumbsUp class="size-3.5" />
                    </Button>
                    <Button
                        size="icon"
                        variant="ghost"
                        class="size-7"
                        :class="feedbackRating === 'not_helpful' ? 'text-destructive' : 'text-muted-foreground'"
                        :aria-pressed="feedbackRating === 'not_helpful'"
                        aria-label="Mark suggestion not helpful"
                        @click="submitFeedback('not_helpful')"
                    >
                        <ThumbsDown class="size-3.5" />
                    </Button>
                </div>
            </div>
        </template>

        <Dialog v-model:open="reviewDialogOpen">
            <DialogContent class="max-h-[min(85svh,42rem)] overflow-hidden sm:max-w-[425px]">
                <DialogHeader class="text-left">
                    <DialogTitle>Review assistant action</DialogTitle>
                    <DialogDescription>
                        Nothing will be created until you confirm this action.
                    </DialogDescription>
                </DialogHeader>

                <div class="min-h-0 space-y-4 overflow-y-auto">
                    <div class="space-y-1.5">
                        <p class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Action</p>
                        <p class="text-sm font-medium">{{ reviewDestinationLabel }}</p>
                        <p class="text-sm leading-6 text-muted-foreground">
                            {{ suggestion.summary }}
                        </p>
                    </div>

                    <div v-if="hasTaskChecklist" class="space-y-2">
                        <p class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {{ selectedTaskCount }} selected task{{ selectedTaskCount === 1 ? '' : 's' }}
                        </p>
                        <ul class="divide-y divide-border/50">
                            <li
                                v-for="task in selectedReviewTasks"
                                :key="task.id"
                                class="py-2.5 text-sm leading-5"
                            >
                                {{ task.title }}
                            </li>
                        </ul>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" @click="reviewDialogOpen = false">Cancel</Button>
                    <Button :disabled="applying || !canActOnSelection" @click="confirmApplySelection">
                        {{ applying ? 'Working...' : 'Confirm action' }}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        <DetailsSheet
            :open="refineDialogOpen"
            title="Refine with AI"
            description="Tell the assistant what should change before you use this suggestion."
            :dirty="Boolean(refinementText.trim())"
            :saving="refining"
            save-label="Update suggestion"
            saving-label="Updating…"
            :save-disabled="!refinementText.trim()"
            @update:open="refineDialogOpen = $event"
            @cancel="refinementText = ''"
            @save="submitRefinement"
        >
            <div class="space-y-5">
                <div class="space-y-2">
                    <label :for="refinementInputId" class="text-sm font-medium">What should change?</label>
                    <Textarea
                        :id="refinementInputId"
                        v-model="refinementText"
                        rows="5"
                        :maxlength="500"
                        :aria-describedby="`${refinementInputId}-help`"
                        class="min-h-32 resize-y"
                        placeholder="For example: make this two tasks, keep it inside the website retainer, or turn this into a fuller project plan."
                    />
                    <div :id="`${refinementInputId}-help`" class="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                        <span>Updating uses one AI action.</span>
                        <span>{{ refinementText.length }} / 500</span>
                    </div>
                </div>

                <div v-if="showProjectSelector" class="space-y-2 border-t border-border/60 pt-5">
                    <label class="text-sm font-medium">Target project</label>
                    <SearchableSelectMenu
                        v-model="selectedProjectId"
                        :options="projectMenuOptions"
                        placeholder="Choose a target project"
                        allow-clear
                        clear-label="Let the assistant decide"
                        show-all-by-default
                        variant="form"
                    />
                    <p class="text-xs leading-5 text-muted-foreground">
                        Choose where the refined suggestion should apply, or leave this empty for the assistant to decide.
                    </p>
                </div>
            </div>
        </DetailsSheet>
    </div>
</template>
